import { FC, useState } from "react";
import WidgetTags from "./WidgetTags";
import Card3 from "./Card3";

export interface SectionLatestPostsProps {
  posts?: any[];
  className?: string;
  postCardName?: "card3";
}

const SectionLatestPosts: FC<SectionLatestPostsProps> = ({
  posts = [],
  postCardName = "card3",
  className = "",
}) => {
  const renderCard = (post: any, index: number) => {
    switch (postCardName) {
      case "card3":
        return (
          <div
            key={post.id}
            className="group relative overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:scale-[1.02] border border-neutral-100 dark:border-neutral-800"
            style={{
              animationDelay: `${index * 100}ms`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <Card3 className="" post={post} />

            {/* Reading Time Badge */}
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100">
              <span className="inline-flex items-center px-2 py-1 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm text-xs font-medium text-neutral-600 dark:text-neutral-400 rounded-full shadow-sm">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                5 min
              </span>
            </div>

            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
          </div>
        );

      default:
        return null;
    }
  };

  const [visibleBlogs, setVisibleBlogs] = useState<number>(9);
  const loadMoreBlogs = () => {
    setVisibleBlogs((prevVisibleBlogs) => prevVisibleBlogs + 6);
  };
  const loadLessBlogs = () => {
    setVisibleBlogs((prevVisibleBlogs) => Math.max(9, prevVisibleBlogs - 6));
  };

  const displayedPosts = Array.isArray(posts) ? posts.slice(4, visibleBlogs) : [];

  return (
    <div className={`nc-SectionLatestPosts relative ${className}`}>
      <div className="flex flex-col lg:flex-row gap-12">
        {/* Main Content */}
        <div className="w-full lg:w-3/5 xl:w-2/3">
          {/* Section Header */}
          <div className="text-center lg:text-left mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-secondary-50 to-accent-50 rounded-full text-sm font-medium text-secondary-600 mb-4">
              <span className="w-2 h-2 bg-secondary-500 rounded-full mr-2 animate-pulse"></span>
              Tous les articles
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                  Explorez notre contenu
                </h2>
                <p className="text-neutral-600 dark:text-neutral-400">
                  Découvrez {posts.length} articles soigneusement sélectionnés
                </p>
              </div>

              {/* View Toggle */}
              <div className="hidden sm:flex items-center space-x-2 bg-neutral-100 dark:bg-neutral-800 rounded-xl p-1">
                <button className="p-2 bg-white dark:bg-neutral-700 rounded-lg shadow-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button className="p-2 text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Articles Grid */}
          <div className="grid gap-8 md:gap-10 grid-cols-1 animate-fade-in-up">
            {displayedPosts.map((post, index) => renderCard(post, index))}
          </div>

          {/* Load More/Less Section */}
          {posts.length > 4 && (
            <div className="mt-16">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                {/* Progress Indicator */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <span>Affichage</span>
                    <span className="font-semibold text-primary-600">
                      {Math.min(visibleBlogs - 4, posts.length - 4)}
                    </span>
                    <span>sur</span>
                    <span className="font-semibold">{posts.length - 4}</span>
                    <span>articles</span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-24 h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(100, ((visibleBlogs - 4) / (posts.length - 4)) * 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-4">
                  {visibleBlogs < posts.length && (
                    <button
                      onClick={loadMoreBlogs}
                      className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      <span>Charger plus</span>
                      <svg
                        className="w-4 h-4 ml-2 transform group-hover:translate-y-0.5 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 14l-7 7m0 0l-7-7m7 7V3"
                        />
                      </svg>
                    </button>
                  )}

                  {visibleBlogs > 9 && (
                    <button
                      onClick={loadLessBlogs}
                      className="inline-flex items-center px-6 py-3 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded-xl font-medium border border-neutral-200 dark:border-neutral-700 hover:border-primary-200 dark:hover:border-primary-700 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-300"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 10l7-7m0 0l7 7m-7-7v18"
                        />
                      </svg>
                      <span>Voir moins</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-2/5 xl:w-1/3">
          <div className="sticky top-8 space-y-8">
            {/* Newsletter Signup */}
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-2xl p-6 border border-primary-100 dark:border-primary-800">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-2">Newsletter</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  Recevez nos derniers articles directement dans votre boîte mail
                </p>
                <div className="space-y-3">
                  <input
                    type="email"
                    placeholder="Votre email"
                    className="w-full px-4 py-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                  <button className="w-full px-4 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 text-sm">
                    S'abonner
                  </button>
                </div>
              </div>
            </div>

            {/* Tags Widget */}
            <WidgetTags />

            {/* Popular Articles */}
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-800">
              <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                Articles populaires
              </h3>
              <div className="space-y-4">
                {posts.slice(0, 3).map((post, index) => (
                  <div key={index} className="flex items-start space-x-3 group cursor-pointer">
                    <div className="flex-shrink-0 w-2 h-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mt-2 group-hover:scale-150 transition-transform duration-300"></div>
                    <div>
                      <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300 line-clamp-2">
                        {post.title || "Titre de l'article"}
                      </h4>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        Il y a {index + 1} jour{index > 0 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionLatestPosts;
